import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { filter } from 'rxjs/operators';
import { Event, NavigationStart, Router } from '@angular/router';
import { QuizStoreService } from 'src/app/services/quiz-store.service';
import { AppConfigService } from 'src/app/services/app-config.service';
import { SavedJobsService } from 'src/app/services/saved-jobs.service';
import { Organization } from 'src/app/classes/organization.class';
import { ScrollToService } from 'src/app/services/scroll-to.service';
import { QUIZ_TYPES } from 'src/app/resources/quiz-types';

interface NavbarRoute {
  link: string;
  translationKey: string;
  active: boolean;
  customLinkName?: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {

  organization: Organization = this.configService.organization;
  jobCompanyLogo: string;
  savedJobsTotal: number;

  routes: NavbarRoute[] = [];

  showApplyButton = false;
  showProgressBar = false;
  showNavigationContainer = false;
  showSocialMediaLinks = false;
  showJobListButton = false;

  showInfoButton = false;
  infoPopupActive = false;
  infoPopupContentTranslationKey = '';

  constructor(
    private configService: AppConfigService,
    private router: Router,
    private quizStore: QuizStoreService,
    private savedJobsService: SavedJobsService,
    private cdr: ChangeDetectorRef,
    private scrollToService: ScrollToService
  ) {
    this.router.events
      .pipe(
        filter((event: Event) => event instanceof NavigationStart)
      )
      .subscribe(({url, navigationTrigger}: NavigationStart) => {
        if (navigationTrigger === 'popstate' && (this.router.url.includes('quiz') || this.router.url.includes('video'))) {
          return;
        }

        this.setActiveRoute(url);

        this.showApplyButton = url.includes('/job-details');
        this.showJobListButton = url.includes('/application-complete');

        if (this.isAllJobs) {
          this.checkIfDisplayJobCompanyLogo(url);
        }

        this.toggleInfoButton(url);
        this.toggleProgressBar(url);
        this.toggleNavigation(url);

        if (this.infoPopupActive) {
          this.infoPopupActive = false;
        }
      });
  }

  get href(): string {
    return window.location.toString();
  }

  get isAllJobs(): boolean {
    return this.configService.isAllJobs;
  }

  ngOnInit(): void {
    this.trackActiveRoutes();
    this.trackSavedJobs();
    this.setNavbarRoutes();
    this.trackCompanyLogoChanges();

    if (
      this.organization.facebookProfileUrl  ||
      this.organization.instagramProfileUrl ||
      this.organization.linkedInProfileUrl ||
      this.organization.tiktokProfileUrl ||
      this.organization.twitterProfileUrl
    ) {
      this.showSocialMediaLinks = true;
    }
  }

  trackCompanyLogoChanges(): void {
    this.configService.jobCompanyLogo$
      .subscribe((jobCompanyLogo: string) => {
        this.jobCompanyLogo = jobCompanyLogo;
      });
  }

  checkIfDisplayJobCompanyLogo(url: string): void {
    const urlsToDisplayJobCompanyLogo = ['/job-details', '/quiz', '/video', '/register', '/application-start', '/application-complete'];
    const containsUrl = !!urlsToDisplayJobCompanyLogo.find((val: string) => url.includes(val));
    const jobCompanyLogo = sessionStorage.getItem('jobCompanyLogo');

    this.jobCompanyLogo = containsUrl && jobCompanyLogo
      ? jobCompanyLogo
      : '';
  }

  trackSavedJobs(): void {
    this.savedJobsService.savedJobsTotal$
      .subscribe(total => this.savedJobsTotal = total);
  }

  trackActiveRoutes(): void {
    this.scrollToService.activeRoute$
      .subscribe((routeLink: string) => {
        this.routes
          .forEach(({link}, index: number, routes: NavbarRoute[]) => {
            if (link === routeLink) {
              routes[index].active = true;
              return;
            }

            routes[index].active = false;
          });
      });
  }

  setNavbarRoutes(): void {
    const landingPage = this.configService.organization.landingPage;

    this.routes = [
      {
        link: 'jobs',
        translationKey: 'HEADER.AUDITIONS',
        active: false,
        customLinkName: this.organization.landingPage ? landingPage?.auditionsLinkLabel : null
      }
    ];

    if (!this.configService.isAllJobs) {
      this.routes.unshift({
        link: 'home',
        translationKey: 'HEADER.HOME',
        active: false,
        customLinkName: this.organization.landingPage ? landingPage?.homeLinkLabel : null
      });
    }

    if (this.organization.useWhyWorkWithUs) {
      this.routes.push({
        link: 'why-work-with-us',
        translationKey: 'HEADER.ABOUT',
        active: false,
        customLinkName: this.organization.landingPage ? landingPage?.aboutLinkLabel : null
      });
    }
  }

  toggleInfoButton(url: string): void {
    const quizType = this.quizStore.quiz?.type;

    this.showInfoButton = (url.includes('/quiz') && quizType !== QUIZ_TYPES.KNOCKOUT_QUESTIONS) ||
                           url.includes('/video');
  }

  toggleProgressBar(url: string): void {
    this.showProgressBar = url.includes('/quiz')                ||
                           url.includes('/video')               ||
                           url.includes('quiz/message')         ||
                           url.includes('application-complete') ||
                           url.includes('application-start');
  }

  toggleNavigation(url: string): void {
    this.showNavigationContainer = this.savedJobsService.checkRoutesToShowNavigationContainer(url);
  }

  toggleInfoPopup(): void {
    this.infoPopupActive = !this.infoPopupActive;

    const quizType = this.quizStore.quiz?.type;

    if (quizType === QUIZ_TYPES.VIDEO_QUESTIONS) {
      this.infoPopupContentTranslationKey = 'INFO_POPUP.VIDEO_QUESTIONS';
    } else if (quizType === QUIZ_TYPES.BUSINESS_QUESTIONS) {
      this.infoPopupContentTranslationKey = 'INFO_POPUP.DEFAULT';
    }

    this.cdr.detectChanges();
  }

  setActiveRoute(url: string): void {
    if (url.includes('/jobs')) {
      this.scrollToService.setActiveRoute('jobs');
    } else if (url.includes('/why-work-with-us')) {
      this.scrollToService.setActiveRoute('why-work-with-us');
    } else if (url.includes('home') && this.scrollToService.activeRoute !== 'home') {
      this.scrollToService.setActiveRoute('home');
    } else {
      this.scrollToService.setActiveRoute('');
    }
  }

  navigate(navToggleInput: HTMLInputElement, routeLink?: string): void {
    navToggleInput.checked = false;

    if (!routeLink) {
      return;
    }

    if (this.router.url.includes('home')) {
      this.scrollToService.setScrollTo(routeLink);

      return;
    }

    this.scrollToService.setActiveRoute(routeLink);
    this.router.navigate([routeLink]);
  }

  removeAnswers(): void {
    sessionStorage.removeItem('answers');
  }
}
